import { authFetch, errorMessage } from "./client";

/**
 * Jednostavan JSON klijent za module bez striktnih tipova odgovora (izvještaji, analitika, DMS...).
 * Automatski dodaje izabranu opštinu iz zaglavlja (`opstina`) na sve zahtjeve prema /api/v1 osim administracije.
 */
let currentOpstina: string | null = null;
export const setRequestOpstina = (c: string | null) => { currentOpstina = c; };
export const getRequestOpstina = () => currentOpstina;

const NO_SCOPE = /^\/api\/v1\/(admin\/|auth\/|system\/(params|integrations|backups|jobs|info)|codelists|help)/;

export function withOpstina(url: string): string {
  if (!currentOpstina || !url.startsWith("/api/v1/") || NO_SCOPE.test(url) || /[?&]opstina=/.test(url)) return url;
  return url + (url.includes("?") ? "&" : "?") + "opstina=" + encodeURIComponent(currentOpstina);
}

type Q = Record<string, string | number | boolean | null | undefined | (string | number)[]>;

export function qs(params?: Q): string {
  if (!params) return "";
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => u.append(k, String(x)));
    else u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

async function handle<T>(r: Response): Promise<T> {
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get("content-type") ?? "";
  const body = ct.includes("json") ? await r.json() : await r.text();
  if (!r.ok) throw new Error(typeof body === "string" ? body || r.statusText : errorMessage(body));
  return body as T;
}

export const http = {
  get: async <T = unknown>(path: string, params?: Q) => handle<T>(await authFetch(withOpstina(path + qs(params)))),
  post: async <T = unknown>(path: string, body?: unknown, params?: Q) =>
    handle<T>(await authFetch(withOpstina(path + qs(params)), {
      method: "POST", headers: { "Content-Type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body),
    })),
  put: async <T = unknown>(path: string, body?: unknown) =>
    handle<T>(await authFetch(withOpstina(path), { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })),
  patch: async <T = unknown>(path: string, body?: unknown, params?: Q) =>
    handle<T>(await authFetch(withOpstina(path + qs(params)), { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body ?? {}) })),
  del: async <T = unknown>(path: string) => handle<T>(await authFetch(withOpstina(path), { method: "DELETE" })),
  form: async <T = unknown>(path: string, fd: FormData) => handle<T>(await authFetch(withOpstina(path), { method: "POST", body: fd })),
};

/** Preuzimanje / otvaranje fajla iz API-ja (PDF/XLSX/CSV) sa tokenom. */
export async function downloadFile(path: string, init: RequestInit = {}, open = false): Promise<Response> {
  const r = await authFetch(withOpstina(path), init);
  if (!r.ok) {
    const t = await r.text();
    let msg = t;
    try { msg = errorMessage(JSON.parse(t)); } catch { /* tekst */ }
    throw new Error(msg || r.statusText);
  }
  const blob = await r.blob();
  const cd = r.headers.get("content-disposition") ?? "";
  const name = /filename="?([^";]+)"?/.exec(cd)?.[1] ?? "dokument";
  const url = URL.createObjectURL(blob);
  if (open) window.open(url, "_blank");
  else {
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return r;
}
